/**
 * TracesCreateEditDrawer Component
 *
 * This component provides a modal interface for creating and editing trace entries.
 * It uses Material-UI components and react-hook-form for form handling.
 *
 * @component
 */

import { Box, Stack, Button, Modal, IconButton } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SHA512 } from 'crypto-js';
import useToast from '../../hooks/useToast';
import { useAppSelector } from '../../store';
import ContainerTitle from '../ui/container-title';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import DatePickerController from '../form/controller/date-picker.controller';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { traceSchema } from '../../interfaces/traces.interface';
import momenttimezone from 'moment-timezone';
import TimePickerController from '../form/controller/time-picker.controller';
import AutoCompleteController from '../form/controller/autocomplete.controller';
import InputFieldsController from '../form/controller/input-fields.controller';
import {
  useCreateTraceMutation,
  useUpdateTraceMutation,
} from '../../api/traces/traces.api.rtk';
import LoadingComponent from '../animation/loading-component';
import {
  selectDepartmentList,
  selectDepartmentListCode,
} from '../../store/selector/property.selector';
import { isSaveDisabled } from '@utils/form';
import { useCurrentModule } from '@hooks/useCurrentModule';

/**
 * Interface for the trace data
 */
export interface TracesData {
  department: string;
  traceDateFrom: object;
  traceDateTo: object;
  traceTime: object;
  displayTime: object;
  message: string;
}

/**
 * TracesCreateEditDrawer component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls the visibility of the modal
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {any} [props.traceData] - Existing trace data for editing
 * @param {any} [props.traceDetails] - Additional trace details
 * @param {string} [props.traceTarget] - Target of the trace
 */
const TracesCreateEditDrawer = ({
  isOpen,
  onClose,
  traceData,
  traceDetails,
  traceTarget,
}: {
  isOpen: boolean;
  onClose: () => void;
  traceData?: any;
  traceDetails?: any;
  traceTarget?: string;
}) => {
  const [createTrace, { isLoading: isCreating }] = useCreateTraceMutation();
  const [updateTrace, { isLoading: isUpdating }] = useUpdateTraceMutation();
  const [_traceDataField, setTraceData] = useState<
    TracesData | null | undefined
  >(null);
  const { toast } = useToast();
  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );

  const departmentlist = useAppSelector(selectDepartmentList);
  const listDepartment = departmentlist.filter(
    (item: any) => item == 'Front Office' || item == 'Users Management',
  );

  const currentModule = useCurrentModule();

  const modulelist = useAppSelector(selectDepartmentListCode)?.map(
    (item: any) => item,
  );

  /**
   * Handles form submission
   * @param {TracesData} data - Form data
   */
  const onSubmit: SubmitHandler<TracesData> = async (data) => {
    const {
      department,
      traceDateFrom,
      traceDateTo,
      traceTime,
      displayTime,
      message,
    } = data;

    const formattedDateFrom = moment(traceDateFrom).format('YYYY-MM-DD');
    const formattedDateTo = moment(traceDateTo).format('YYYY-MM-DD');
    const formattedTraceTime = moment(traceTime, 'HH:mm:ss').format('HH:mm');
    const formattedDisplayTime = moment(displayTime, 'HH:mm:ss').format(
      'HH:mm',
    );

    const dept = modulelist?.find((item) => item?.Description === department);
    const cm = modulelist?.find((item) => item?.Code === currentModule);
    const signature = SHA512(
      selectedProperty.value +
        (traceData ? traceData?.trace?.traceID : '') +
        (cm?.id !== undefined ? cm?.id : '') +
        (dept?.id !== undefined ? dept?.id : '') +
        (traceDetails ? traceDetails : '') +
        message +
        formattedDateFrom +
        formattedDateTo +
        formattedTraceTime +
        formattedDisplayTime,
    ).toString();
    const traces = {
      properties: [
        {
          property: {
            propertyID: selectedProperty.value,
            traces: [
              {
                trace: {
                  traceID: traceData?.trace?.traceID
                    ? traceData?.trace?.traceID
                    : 0,
                  cmID: cm?.id !== undefined ? cm?.id : '',
                  departmentID: dept?.id !== undefined ? dept?.id : '',
                  traceTarget: traceTarget ? traceTarget : 'Generic',
                  referenceID: traceDetails ? traceDetails : '',
                  message: message,
                  recurringDateFrom: formattedDateFrom,
                  recurringDateTo: formattedDateTo,
                  recurringTime: formattedTraceTime,
                  displayTime: formattedDisplayTime,
                  signature: signature,
                },
              },
            ],
          },
        },
      ],
    };
    if (traceData) {
      return await updateTrace(traces)
        .unwrap()
        .then(() => {
          (toast({
            severity: 'success',
            message: 'Success Updating of Traces',
          }),
            reset());
        })
        .catch((err) =>
          toast({
            severity: 'error',
            message: err,
          }),
        )
        .finally(onClose);
    }
    await createTrace(traces)
      .unwrap()
      .then(() => {
        (toast({ severity: 'success', message: 'Success Creating of Traces' }),
          reset());
      })
      .catch((err) =>
        toast({
          severity: 'error',
          message: err,
        }),
      )
      .finally(onClose);
  };

  const methods = useForm<TracesData>({
    defaultValues: {
      traceDateFrom: momenttimezone().tz('Asia/Manila'),
      traceDateTo: momenttimezone().tz('Asia/Manila'),
      traceTime: moment().set({ hour: 0, minute: 0 }), // Set to midnight
      displayTime: moment().set({ hour: 0, minute: 0 }), // Set to midnight
    },
    resolver: yupResolver<TracesData>(traceSchema),
    mode: 'onSubmit',
  });

  const [_selectedTracesDateFrom, setSelectedTracesDateFrom] =
    useState<Moment | null>(moment());
  const [_selectedTracesDateTo, setSelectedTracesDateTo] =
    useState<Moment | null>(moment());
  const [_selectedTraceTime, setSelectedTraceTime] = useState<Moment | null>(
    moment(),
  );
  const [_selectedDisplayTime, setSelectedDisplayTime] =
    useState<Moment | null>(moment());
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  /**
   * Effect to populate form with existing trace data when editing
   */
  useEffect(() => {
    if (traceData && isOpen) {
      (async () => {
        try {
          setTraceData(traceData);
          setValue('department', traceData?.trace?.department);
          moment(traceData?.trace?.dateFrom).tz('Asia/Manila')?.isValid() &&
            setValue(
              'traceDateFrom',
              moment(traceData?.trace?.dateFrom).tz('Asia/Manila'),
            );
          moment(traceData?.trace?.dateTo).tz('Asia/Manila')?.isValid() &&
            setValue(
              'traceDateTo',
              moment(traceData?.trace?.dateTo).tz('Asia/Manila'),
            );
          const recurringTime = moment.tz(
            traceData?.trace?.recurringTime,
            'HH:mm',
            'Asia/Manila',
          );
          const displayTime = moment.tz(
            traceData?.trace?.displayTime,
            'HH:mm',
            'Asia/Manila',
          );
          setValue('traceTime', recurringTime);
          setValue('displayTime', displayTime);
          setValue('message', traceData?.trace?.message);
        } catch (error) {
          console.log(error);
        }
      })();
    }
    return () => {
      setTraceData(null);
    };
  }, [traceData, isOpen]);

  return (
    <FormProvider {...methods}>
      <>
        <Modal
          open={isOpen}
          onClose={() => {
            onClose();
            reset();
          }}
          aria-labelledby='modal-title'
          aria-describedby='modal-description'
          id='traces-create-edit_modal'
        >
          <>
            {(isCreating || isUpdating) && <LoadingComponent />}
            <Stack
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%', // Set width to 90% of the viewport width
                maxWidth: 750, // Limit maximum width to 1214 pixels
                bgcolor: 'background.paper',
                boxShadow: 24,
                overflow: 'auto', // Add overflow property to enable scrolling when content overflows
                '@media (max-width: 600px)': {
                  width: '95%', // Adjust width for smaller screens
                  maxWidth: '100%', // Remove max width restriction for smaller screens
                  height: '80vh', // Adjust height for smaller screens
                  maxHeight: 'none', // Remove max height restriction for smaller screens
                },
              }}
            >
              <IconButton
                id='traces-create-edit_modal-close-btn'
                onClick={() => {
                  onClose();
                  reset();
                }}
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 15,
                  zIndex: 1,
                }}
              >
                <CloseIcon />
              </IconButton>
              <Stack>
                <Box sx={{ p: '20px', borderBottom: '1px solid #EEE' }}>
                  <ContainerTitle
                    title={traceData ? 'Edit Traces Entry' : 'Traces Entry'}
                    titleStyle={{ fontSize: '20px!important' }}
                  />
                </Box>
              </Stack>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack flexDirection='row'>
                  <Stack
                    padding='var(--2, 16px) var(--2, 16px) var(--2, 16px) var(--3, 24px)'
                    flexDirection='column'
                    alignItems='flex-start'
                    gap='var(--2, 14px)'
                    sx={{
                      border: '1px dotted #ccc', // Add border style here
                    }}
                  >
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      width='50%'
                    >
                      <AutoCompleteController
                        control={control}
                        errors={errors}
                        label='Department*'
                        name='department'
                        options={listDepartment || []}
                        defaultValue={null}
                        style={{ margin: 0 }}
                        borderErrorOnly
                      />
                    </Stack>
                    <Stack direction='row' alignItems='center' gap='16px'>
                      <DatePickerController
                        control={control}
                        name='traceDateFrom'
                        label='Trace Date From*'
                        addOnChange={(newValue: Moment | null) => {
                          setSelectedTracesDateFrom(newValue);

                          const currentTo = watch('traceDateTo');

                          // If Trace Date To exists and is before new From date → move it forward
                          if (
                            newValue &&
                            currentTo &&
                            moment(newValue).isAfter(moment(currentTo), 'day')
                          ) {
                            setValue('traceDateTo', newValue, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        }}
                        disablePast={true}
                        disabled={moment().isAfter(
                          moment(watch('traceDateFrom')),
                          'day',
                        )}
                      />
                      <DatePickerController
                        control={control}
                        name='traceDateTo'
                        label='Trace Date To*'
                        addOnChange={(newValue: any) =>
                          setSelectedTracesDateTo(newValue)
                        }
                        disablePast={false}
                        minDate={moment(watch('traceDateFrom'))}
                      />
                      <TimePickerController
                        control={control}
                        name='traceTime'
                        label='Trace Time*'
                        addOnChange={(newValue: any) =>
                          setSelectedTraceTime(newValue)
                        }
                        disablePast={false}
                      />
                      <TimePickerController
                        control={control}
                        name='displayTime'
                        label='Notification Time*'
                        addOnChange={(newValue: any) =>
                          setSelectedDisplayTime(newValue)
                        }
                        disablePast={false}
                      />
                    </Stack>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      width='100%'
                    >
                      <InputFieldsController
                        control={control}
                        errors={errors}
                        label='Trace Message*'
                        multiline
                        rows={5}
                        name='message'
                        wrapperStyle={{
                          marginBottom: 0,
                        }}
                        inputStyle={{
                          '.MuiInputBase-root': { height: '' },
                        }}
                        borderErrorOnly
                        showTextLength
                      />
                    </Stack>
                  </Stack>
                </Stack>
                <Stack
                  sx={{ p: '10px', position: 'sticky', top: '0', zIndex: 1 }}
                >
                  <Stack
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='flex-end'
                    gap={1}
                  >
                    <Button
                      onClick={() => {
                        onClose();
                        reset();
                      }}
                      variant='outlined'
                      color='primary'
                    >
                      Close
                    </Button>
                    <LoadingButton
                      id='traces-create-edit_save-btn'
                      variant='contained'
                      type='submit'
                      loading={isCreating || isUpdating}
                      disabled={isSaveDisabled(errors)}
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                </Stack>
              </form>
            </Stack>
          </>
        </Modal>
      </>
    </FormProvider>
  );
};

export default TracesCreateEditDrawer;
